import React from 'react';
import { Select, Space } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { Moment } from 'moment';
import {
	Currencies,
	DATE_AND_TIME,
	PeriodTypes,
} from '@shared/utils/constants';
import { toMoment } from '@shared/utils/date';
import { Values } from '@shared/utils/objectEnums';
import type {
	PeriodSchedule,
	PeriodSchedule as PeriodScheduleType,
} from '@api/features/fixtures/getFixtureDetails';
import type { UpdatePeriodScheduleItemRequest } from '@api/features/fixtures/updatePeriodScheduleItem';
import type TcTerms from '@api/models/tc-terms';
import CurrencyInput from '@client/components/CurrencyInput';
import DatePicker from '@client/components/DatePicker';
import PeriodSelector, { PeriodValue } from '@client/components/PeriodSelector';
import Button from '@client/components/Button';
import { renderUtcDate } from '@client/utils/table';

type PeriodOnChange = { value: PeriodValue; onChange: (n: PeriodValue) => void };
type NumberOnChange = { value: number | null; onChange: (n: number | null) => void };
type ExactDateOnChange = { value: boolean; row: PeriodSchedule; };

export const periodScheduleDateColumn = (
	dataIndex: 'from' | 'to' | 'maxTo',
	title: string,
	width?: number,
) => ({
	title,
	dataIndex,
	editable: false,
	type: 'date' as const,
	...(width != null ? { width } : {}),
	renderValue: ({ value }: { value: Moment | null }) => renderUtcDate(value, DATE_AND_TIME),
});

export const getModalTableColumns = ({
	periodSchedule,
	tcTerms,
	fixtureCurrency,
	deleteScheduleItem,
	disabled,
	patchDraftRow,
}: {
	disabled: boolean;
	fixtureCurrency: Values<typeof Currencies>;
	periodSchedule: PeriodSchedule[];
	tcTerms: TcTerms;
	deleteScheduleItem: (id: number) => void;
	patchDraftRow: (id: number, attributes: Partial<UpdatePeriodScheduleItemRequest['attributes']>) => void;
}) =>
	[
		{
			title: '#',
			editable: false,
			type: 'text',
			dataIndex: 'order',
		},
		{
			title: 'Period type',
			dataIndex: 'periodType',
			editable: (row: PeriodSchedule) => {
				const previousItem = periodSchedule.find((e) => e.order === row.order - 1);

				if (row.order === 1) {
					return false;
				}

				if (previousItem != null && previousItem.periodType === PeriodTypes.OPTIONAL) {
					return false;
				}

				return true;
			},
			type: 'select',
			inputProps: {
				disabled: disabled,
				placeholder: 'Select period type',
				options: [
					{
						label: 'Optional',
						value: 'optional',
					},
					{
						label: 'Declared',
						value: 'declared',
					},
				],
			},
		},
		{
			title: 'Hire rate',
			dataIndex: 'hireRate',
			editable: true,
			type: 'currency',
			// @ts-ignore
			inputProps: {},
			renderInput: ({ value, onChange }: NumberOnChange) => {
				return (
					<CurrencyInput
						disabled={disabled}
						required
						asAsync
						currency={fixtureCurrency}
						addonAfter={tcTerms.hireUnit ? '/day' : '/month'}
						value={value}
						onChange={onChange}
						style={(value == null || value === 0) ? { border: '1px solid red' } : undefined}
					/>
				);
			},
		},
		{
			title: 'Hire type',
			dataIndex: 'hireType',
			editable: true,
			type: 'select',
			inputProps: {
				disabled: disabled,
				placeholder: 'Select hire type',
				options: [
					{
						label: 'Fixed',
						value: 'fixed',
					},
					{
						label: 'Floating',
						value: 'floating',
					},
				],
			},
		},
		{
			title: 'Description',
			dataIndex: 'description',
			editable: true,
			type: 'text',
			inputProps: {
				disabled: disabled,
				placeholder: 'Description',
			},
		},
		{
			title: 'Exact date',
			dataIndex: 'useExactDate',
			editable: true,
			type: 'select',
			inputProps: {
				disabled,
				placeholder: 'Select',
				options: [
					{ label: 'Yes', value: true },
					{ label: 'No', value: false },
				],
			},
			renderInput: ({ value, row }: ExactDateOnChange) => (
				<Select
					disabled={disabled}
					value={value}
					options={[
						{ label: 'Yes', value: true },
						{ label: 'No', value: false },
					]}
					onChange={(nextValue) => {
						if (nextValue === value) {
							return;
						}

						// Update the entire row in one operation.
						// Avoid calling onChange here, which could overwrite the patch.
						patchDraftRow(row.id, {
							useExactDate: nextValue,
							minimumDurationExact: nextValue ?
								row.minimumDurationExact ?? row.to :
								null,
							maximumDurationExact: nextValue ?
								row.maximumDurationExact ?? (row.maxTo != null ? toMoment(row.maxTo).utc() : null) :
								null,
						});
					}}
				/>
			),
		},
		{
			title: 'Period End (min.)',
			editable: true,
			dataIndex: 'minPeriod',
			type: 'period',
			transformData: {
				in: (_v: any, row: PeriodSchedule) => [
					row.durationMinimumValue,
					row.durationMinimumUnit,
					row.durationMinimumVariance,
					row.minimumDurationExact,
					row.id,
				],
				out: ([periodValue, unit, variance, exactDate]: any[]) => ({
					durationMinimumVariance: variance,
					durationMinimumValue: periodValue === '' ? null : periodValue,
					durationMinimumUnit: unit,
					minimumDurationExact: exactDate ?? null,
				}),
			},
			renderInput: ({ value, onChange }: PeriodOnChange) => {
				const exactDate = (value as any[])[3];
				const id = Number((value as any[])[4]);
				const row = periodSchedule.find((item) => item.id === id);

				return (
					<Space size={0}>
						{row?.useExactDate ? (
							<DatePicker
								time
								disabled={disabled}
								value={exactDate != null ? toMoment(exactDate) : null}
								onChange={(val) => {
									const next = [...(value as any[])];
									next[3] = val;
									onChange(next as any);
									patchDraftRow(id, { minimumDurationExact: val as Moment });
								}}
							/>
						) : (
							<PeriodSelector
								disabled={disabled}
								value={value}
								onChange={(nextVal) => {
									onChange(nextVal);

									const [p, u, v] = nextVal as any[];
									patchDraftRow(id, {
										durationMinimumValue: p === '' ? null : p,
										durationMinimumUnit: u,
										durationMinimumVariance: v,
										minimumDurationExact: null,
									});
								}}
							/>
						)}
					</Space>
				);
			},
		},
		{
			title: 'Period End (max)',
			editable: true,
			dataIndex: 'maxPeriod',
			type: 'period',
			transformData: {
				in: (_v: any, row: PeriodSchedule) => [
					row.durationMaximumValue,
					row.durationMaximumUnit,
					row.durationMaximumVariance,
					row.maximumDurationExact,
					row.id,
				],
				out: ([periodValue, unit, variance, exactDate]: any[]) => ({
					durationMaximumVariance: variance,
					durationMaximumValue: periodValue === '' ? null : periodValue,
					durationMaximumUnit: unit,
					maximumDurationExact: exactDate ?? null,
				}),
			},
			renderInput: ({ value, onChange }: PeriodOnChange) => {
				const exactDate = (value as any[])[3];
				const id = Number((value as any[])[4]);
				const row = periodSchedule.find((item) => item.id === id);

				return (
					<Space size={0}>
						{row?.useExactDate ? (
							<DatePicker
								time
								disabled={disabled}
								value={exactDate != null ? toMoment(exactDate) : null}
								onChange={(val) => {
									const next = [...(value as any[])];
									next[3] = val;
									onChange(next as any);
									patchDraftRow(id, { maximumDurationExact: val as Moment });
								}}
							/>
						) : (
							<PeriodSelector
								disabled={disabled}
								value={value}
								onChange={(nextVal) => {
									onChange(nextVal);

									const [p, u, v] = nextVal as any[];
									patchDraftRow(id, {
										durationMaximumValue: p === '' ? null : p,
										durationMaximumUnit: u,
										durationMaximumVariance: v,
										maximumDurationExact: null,
									});
								}}
							/>
						)}
					</Space>
				);
			},
		},
		periodScheduleDateColumn('from', 'Start', 100),
		periodScheduleDateColumn('to', 'End (min)', 100),
		periodScheduleDateColumn('maxTo', 'End (max)', 100),
		{
			title: '',
			width: 25,
			editable: false,
			render: (row: PeriodScheduleType) => (
				<Button
					type="link"
					danger
					disabled={row.order === 1 || disabled}
					onClick={() => deleteScheduleItem(row.id)}
					icon={(<DeleteOutlined />)}
				/>
			),
		},
	];
